import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Anchor, Crown, Users, Check, Clock, MapPin, Sparkles, GlassWater, Phone, Calendar, AlertCircle, ChevronDown, UtensilsCrossed, Wine } from "lucide-react";
import SEOHead from "../components/SEOHead";
import Starfield from "../components/Starfield";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { MAGIC_PHOTOS, CONTACT } from "../data/cruiseData";

// All rates and menus are taken verbatim from magiccruises.com.au/cruise-menu-pricing/
// Single source of truth for Magic Hire Rates, the page surfaces every cost line + menu in
// accordions for easy scanning. No SEO-only language here, this is the operations rate card.

const accent = "hsl(45, 100%, 72%)"; // gold
const hot = "hsl(320, 90%, 72%)"; // pink
const tint = "hsl(180, 75%, 70%)"; // cyan
const star = "hsl(45, 100%, 78%)";
const dark1 = "#0A0A12";
const dark2 = "#0F0F1A";

export default function CruisePricingMenus() {
 return (
  <div className="bg-[#070710] text-white" data-testid="cruise-pricing-menus-page">
   <SEOHead
    title="Magic Cruises Rates & Menus | Sydney Harbour Boat Hire Pricing & Catering"
    description="Magic Cruises boat hire pricing for Sydney Harbour cruises. Vessel hire rates, canapé and buffet menus (Crystal, Emerald, Diamond), beverage packages, bar tab options, wharf fees, staff charges, public holiday surcharges and entertainment add-ons. The complete Magic Hire Rates card."
    canonical="https://magiccruises.com.au/rates-and-menus"
    includeLocalBusiness={true}
   />

   <FloatingSectionNav />

   {/* HERO */}
   <section className="relative isolate overflow-hidden border-b border-white/10" style={{ background: `radial-gradient(circle at 30% 40%, ${dark2} 0%, ${dark1} 70%)` }}>
    <Starfield density={28} color={star} glow={`${star}55`} fixed={false} intensity={0.6} />
    <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(circle at 75% 25%, ${accent}1A 0%, transparent 55%)` }} />
    <div className="absolute inset-0 pointer-events-none" style={{ background: `radial-gradient(circle at 20% 80%, ${hot}1C 0%, transparent 55%)` }} />

    <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-10 py-24 lg:py-32">
     <div className="max-w-3xl">
      <Eyebrow>Magic hire rates · Sydney harbour</Eyebrow>
      <h1 className="font-black tracking-tight leading-[0.92] mt-6" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.4rem, 6vw, 5rem)" }} data-testid="rates-menus-h1">
       Magic Hire <span className="italic font-light bg-clip-text text-transparent inline-block" style={{ backgroundImage: `linear-gradient(90deg, ${accent} 0%, ${hot} 60%, ${tint} 100%)`, paddingRight: "0.12em" }}>Rates &amp; Menus.</span>
      </h1>
      <p className="text-white/82 text-lg lg:text-xl mt-7 font-light leading-relaxed">
       The full Magic Cruises operations rate card, every vessel hire rate, every catering menu, every beverage option, every wharf fee, every staff charge, every entertainment add-on. Tap any panel to see the line by line breakdown.
      </p>
     </div>
     <div className="mt-12 max-w-6xl" data-testid="rates-jump-nav">
      <div className="text-[10px] uppercase tracking-[0.32em] font-black text-white/55 mb-5">Jump to · tap any panel below</div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
       <JumpButton
        href="#vessel-hire"
        icon={Anchor}
        label="Vessel Hire Rates"
        sub="Per-hour boat charter pricing"
        gradient={`linear-gradient(135deg, ${tint} 0%, ${accent} 100%)`}
        shadow={`0 24px 60px -14px ${tint}AA`}
        testid="rates-hero-cta-rates"
       />
       <JumpButton
        href="#menus"
        icon={UtensilsCrossed}
        label="Menus"
        sub="Canapé · Buffet · Crystal · Emerald · Diamond"
        gradient={`linear-gradient(135deg, ${hot} 0%, ${accent} 100%)`}
        shadow={`0 24px 60px -14px ${hot}AA`}
        testid="rates-hero-cta-menus"
       />
       <JumpButton
        href="#beverages"
        icon={Wine}
        label="Beverage Package"
        sub="Bar tab, packages, spirits & wines"
        gradient={`linear-gradient(135deg, ${tint} 0%, ${hot} 100%)`}
        shadow={`0 24px 60px -14px ${hot}99`}
        testid="rates-hero-cta-beverages"
       />
      </div>
     </div>
    </div>
   </section>

   {/* HIGHLIGHTS STRIP */}
   <section className="relative border-b border-white/10 py-9 px-5 lg:px-10" style={{ background: dark2 }} data-testid="rates-highlights">
    <div className="mx-auto max-w-7xl grid grid-cols-2 lg:grid-cols-4 gap-6">
     {[
      { icon: Crown, label: "20+ YEARS", sub: "Sydney Harbour events" },
      { icon: Users, label: "UP TO 100", sub: "Max catered guests" },
      { icon: Anchor, label: "KING ST WHARF 3", sub: "Darling Harbour boarding" },
      { icon: GlassWater, label: "FULLY LICENSED", sub: "Bar travels with you" }].map(({ icon: Icon, label, sub }, i) => (
      <div key={i} className="flex items-start gap-4 px-4 py-2" data-testid={`rates-highlight-${i}`}>
       <Icon className="w-7 h-7 mt-1 shrink-0" style={{ color: accent }} strokeWidth={1.5} />
       <div className="min-w-0">
        <div className="text-white font-black text-sm uppercase tracking-[0.18em]">{label}</div>
        <div className="text-white/55 text-[11px] uppercase tracking-[0.18em] mt-1">{sub}</div>
       </div>
      </div>
     ))}
    </div>
   </section>

   {/* INTRO PARAGRAPH */}
   <section className="relative py-20 lg:py-28 px-5 lg:px-10">
    <div className="mx-auto max-w-4xl">
     <Eyebrow>How the Magic books</Eyebrow>
     <h2 className="font-black tracking-tight leading-[0.96] mt-5" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
      One private boat charter, <em className="font-light italic" style={{ color: accent }}>built around your event.</em>
     </h2>
     <p className="text-white/78 text-base lg:text-lg leading-[1.85] mt-7 font-light">
      The Magic team brings over twenty years of Sydney Harbour event experience to every booking, ensuring a first class experience as you explore the beautiful beaches and bays around the harbour. Available also is the option to anchor for lunch or dinner in a sheltered and peaceful location selected by our Captain. Whatever your occasion, the Magic is sure to impress and leave a lasting impression.
     </p>
     <p className="text-white/78 text-base lg:text-lg leading-[1.85] mt-5 font-light">
      Every Magic Cruises booking is a private full boat charter. The rate card below covers the standard January to October pricing, the November to December peak summer pricing, public holiday surcharges, all catering menus (canapé, mini buffet, Crystal, Emerald, Diamond), beverage and bar options, wharf fees, staff charges and entertainment add-ons. For tailored quotes that combine multiple line items, send an enquiry, we'll come back inside 24 hours with the full event breakdown.
     </p>
    </div>
   </section>

   {/* VESSEL HIRE RATES */}
   <Section id="vessel-hire" eyebrow="Cruise boat hire" heading="Vessel" headingEm="hire pricing." testid="rates-vessel-hire">
    <Accordion type="multiple" defaultValue={["std", "peak", "ph"]} className="space-y-3">
     <RateAccordion value="std" label="January to October standard pricing" data-testid="rates-acc-std">
      <RateGrid items={[
       ["3-hour cruise", "$2,000.00"],
       ["4-hour cruise", "$2,400.00"],
       ["Each additional hour", "$650 per hour"]]} />
     </RateAccordion>
     <RateAccordion value="peak" label="November to December peak summer pricing" data-testid="rates-acc-peak">
      <RateGrid items={[
       ["3-hour cruise", "$2,900.00"],
       ["4-hour cruise", "$3,500.00"],
       ["Each additional hour", "$850 per hour"]]} />
     </RateAccordion>
     <RateAccordion value="cap" label="Maximum catered charter capacity" data-testid="rates-acc-cap">
      <p className="text-white/85 text-base">Up to <strong className="text-white">100 guests</strong> for catered functions on board the Magic.</p>
     </RateAccordion>
     <RateAccordion value="ph" label="Public holiday rates (Minimum 4 hours)" data-testid="rates-acc-ph">
      <p className="text-white/85 text-base">A <strong className="text-white">20% surcharge</strong> is applied to the total booking on public holidays. Minimum 4-hour charter applies.</p>
     </RateAccordion>
    </Accordion>
   </Section>

   {/* WHARF + STAFF + EXTRAS */}
   <Section id="wharf-staff" eyebrow="Operational fees" heading="Wharf, staff" headingEm="and entertainment." testid="rates-wharf-staff">
    <Accordion type="multiple" defaultValue={["wharf", "staff"]} className="space-y-3">
     <RateAccordion value="wharf" label="Wharf fees" data-testid="rates-acc-wharf">
      <RateGrid items={[["City wharfs", "$55.00 per wharf"]]} />
     </RateAccordion>
     <RateAccordion value="staff" label="Staff charges (per cruise, 3 to 4 hours)" data-testid="rates-acc-staff">
      <p className="text-white/75 text-sm uppercase tracking-[0.18em] mb-3"><strong className="text-white">$250</strong> per staff member · applies to all bookings</p>
      <RateGrid items={[
       ["1 to 25 guests", "1 × bar / wait staff"],
       ["26 to 50 guests", "2 × bar / wait staff"],
       ["51+ guests", "3 × bar / wait staff"]]} />
     </RateAccordion>
     <RateAccordion value="ent" label="Entertainment optional extras" data-testid="rates-acc-ent">
      <RateGrid items={[
       ["DJ", "$550.00"],
       ["Photographer", "$650.00"],
       ["Magicians", "$1,800.00"],
       ["Jetboat rides", "$1,850.00"],
       ["Clay pigeon shooting", "$1,400.00"]]} />
     </RateAccordion>
     <RateAccordion value="sec" label="Security & 18th, 21st birthday party requirements" data-testid="rates-acc-sec">
      <ul className="space-y-2 text-white/85 text-base">
       <li className="flex items-start gap-3"><Check className="w-4 h-4 mt-1 shrink-0" style={{ color: accent }} /><span>Security guard <strong className="text-white">required</strong> for 18th to 21st birthday parties</span></li>
       <li className="flex items-start gap-3"><Check className="w-4 h-4 mt-1 shrink-0" style={{ color: accent }} /><span>1 security guard required · <strong className="text-white">$350</strong> for up to 4 hours</span></li>
       <li className="flex items-start gap-3"><Check className="w-4 h-4 mt-1 shrink-0" style={{ color: accent }} /><span><strong className="text-white">Maximum 50 guests</strong> for all 18th, 21st birthday parties</span></li>
       <li className="flex items-start gap-3"><AlertCircle className="w-4 h-4 mt-1 shrink-0" style={{ color: hot }} /><span><strong className="text-white">No bucks cruises</strong> accepted</span></li>
      </ul>
     </RateAccordion>
    </Accordion>
   </Section>

   {/* MENUS */}
   <section id="menus" className="relative py-20 lg:py-28 px-5 lg:px-10 overflow-hidden border-y border-white/10 scroll-mt-24" style={{ background: `linear-gradient(180deg, ${dark1}, ${dark2}, ${dark1})` }} data-testid="rates-menus-section">
    <div className="mx-auto max-w-5xl">
     <Eyebrow>Cruise menu options</Eyebrow>
     <h2 className="font-black tracking-tight leading-[0.96] mt-5" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
      Six catering tiers, <em className="font-light italic" style={{ color: hot }}>built fresh on board.</em>
     </h2>
     <p className="text-white/72 text-base lg:text-lg mt-6 font-light leading-relaxed">Every catering menu is prepared by the on-board chef. Canapé menus start from $30 per person. Buffet menus start from $50 per person. Custom dietary requirements and cultural menus are accommodated on request, let us know on the enquiry form.</p>

     <Accordion type="multiple" className="mt-10 space-y-3">
      <MenuAccordion
       value="canape-set"
       title="Set canapé menu"
       price="$30 per person"
       sections={[{
        items: [
         "Petite quiche, smokey bacon and cheese + ricotta, spinach & sundried tomatoes", "Mini party pies, beef mini pies (hot)", "Vegetarian spring rolls, oven baked with sweet chilli sauce (hot · veg)"],
       }]}
      />
      <MenuAccordion
       value="canape-abc"
       title="Canapé menus A · B · C"
       price="Minimum 20 guests · Standing service up to 95 guests"
       sections={[
        { heading: "Tier pricing", items: [
         "Menu A · $45 pp · 4 canapés (2 hot + 2 cold)", "Menu B · $55 pp · 6 canapés (3 hot + 3 cold)", "Menu C · $65 pp · 8 canapés (4 hot + 4 cold)"]},
        { heading: "Cold canapés", items: [
         "Mini bruschetta, garlic oil rubbed croutons topped with freshly sliced tomato, Spanish onion and handpicked basil leaves, finished with a balsamic glaze (veg)", "Smoked salmon blinis, fresh Tassie smoked salmon served on a dill infused blini, topped with preserved lemon crème fraîche", "Prawn cocktail, fresh prawns served on a bed of shredded lettuce, topped with a Mary Rose mayo", "Assorted sushi, hand rolled assorted sushi and nori rolls including vegetarian options, served with wasabi and soy sauce", "Falafel kebab, homemade falafel topped with a pitted black olive and a cherry tomato (veg · gf)", "Zucchini corn fritters, homemade crispy zucchini corn fritters served with crème fraîche (veg · gf)"]},
        { heading: "Hot canapés", items: [
         "Gourmet party pies, selection of beef burgundy + chicken and leek pies", "Petite quiche, smokey bacon and cheese + ricotta, spinach & sundried tomatoes", "Vegetarian spring rolls, oven baked, served with sweet chilli sauce (veg)", "Meatballs, traditional meatballs served with fresh garden tomato relish", "Spicy Thai fish cakes, delicate fish minced with red curry paste, fish sauce, fresh scallions, served with sweet chilli sauce", "Koftas, fresh mince seasoned with herbs, onion and garlic, served with traditional tzatziki", "Potato wedges, seasoned potato wedges served with sour cream and sweet chilli (veg)", "Hand rolled prawn cones with sweet chilli dipping sauce", "Salt and pepper squid pieces with wasabi mayonnaise", "Cheese and spinach triangles with a yoghurt and cucumber dip", "Assorted mini pizzas"]}]}
      />
      <MenuAccordion
       value="mini-buffet"
       title="Cruising canapé / Mini buffet menu"
       price="$50 per person · Standing or informal seated · 20 to 80 guests"
       sections={[
        { heading: "Canapés", items: [
         "Gourmet party pies, beef burgundy + chicken and leek (hot)", "Vegetarian spring rolls, oven baked, sweet chilli sauce (hot · veg)"]},
        { heading: "Mini buffet", items: [
         "Oven roasted chicken breast, BBQ marinated, served warm", "Italian pasta salad, penne with Spanish onion, cherry tomatoes, Kalamata olives and mozzarella in Italian herbed dressing", "Classic potato salad, sour cream and shallot, topped with crispy bacon bits", "Fresh garden salad, mixed leaves in French dressing with capsicum, carrot, Spanish onion and tomato", "Selection of bakery rolls with individual butter portions"]}]}
      />
      <MenuAccordion
       value="crystal"
       title="Crystal Buffet menu"
       price="$62 per person · Standing or informal seated up to 80 guests · Formal seated up to 42 guests"
       sections={[
        { heading: "Canapés", items: [
         "Gourmet party pies, beef burgundy + chicken and leek (hot)", "Vegetarian spring rolls, oven baked, sweet chilli sauce (hot · veg)"]},
        { heading: "Main meal", items: [
         "Oven roasted chicken breast, BBQ marinated, served warm", "Cold meats platter, smoked champagne ham, mild Hungarian salami, roasted turkey breast, chicken breast", "Italian pasta salad, penne with Spanish onion, cherry tomatoes, Kalamata olives and mozzarella in Italian herbed dressing", "Classic potato salad, sour cream and shallot, topped with crispy bacon bits", "Fresh garden salad, mixed leaves in French dressing with capsicum, carrot, Spanish onion and tomato", "Selection of bakery rolls with individual butter portions"]}]}
       highlight={true}
      />
      <MenuAccordion
       value="emerald"
       title="Emerald Buffet menu"
       price="$72 per person · Standing or informal seated up to 80 guests · Formal seated up to 42 guests"
       sections={[
        { heading: "Canapés", items: [
         "Spicy Thai fish cakes, delicate fish minced with red curry paste, fish sauce, fresh scallions, sweet chilli sauce", "Assorted sushi, hand rolled assorted sushi and nori rolls including vegetarian options, served with wasabi and soy sauce"]},
        { heading: "Main meal", items: [
         "Whole tiger prawns, catch of the day, fresh whole tiger prawns served with freshly cut lemons", "Half shell NZ mussels, served warm in a lemon grass and coconut curry, topped with coriander", "Cold meats platter, smoked champagne ham, mild Hungarian salami, roasted turkey breast, chicken breast", "Italian pasta salad, penne with Spanish onion, cherry tomatoes, Kalamata olives and mozzarella in Italian herbed dressing", "Classic potato salad, sour cream and shallot, topped with crispy bacon bits", "Fresh garden salad, mixed leaves in French dressing with capsicum, carrot, Spanish onion and tomato", "Selection of bakery rolls with individual butter portions"]}]}
       highlight={true}
      />
      <MenuAccordion
       value="diamond"
       title="Diamond Buffet menu"
       price="$95 per person · Minimum $1,800 menu spend · Formal seating for up to 40 guests only"
       sections={[
        { heading: "Canapés (select 3)", items: ["1 hot + 2 cold from the canapé list"]},
        { heading: "Main meal", items: [
         "Oven roasted chicken breast, BBQ marinated, served warm", "Roast sirloin, oven roasted, encrusted with garlic and rosemary, cooked medium rare", "Half shell NZ mussels, served warm in a lemon grass and coconut curry, topped with coriander", "Tasmanian smoked salmon, Tassie smoked salmon slices served with thinly sliced Spanish onions, capers, and a preserved lemon crème fraîche", "Whole tiger prawns, catch of the day, fresh whole tiger prawns served with freshly cut lemons", "Classic potato salad, sour cream and shallot, topped with crispy bacon bits", "Fresh garden salad, mixed leaves in French dressing with capsicum, carrot, Spanish onion and tomato", "Selection of artisan bakery rolls with individual butter portions"]},
        { heading: "Dessert", items: [
         "Cookies and cream cheesecake, individual Oreo cheesecake rounds, assorted vanilla & chocolate"]}]}
       highlight={true}
      />
     </Accordion>
    </div>
   </section>

   {/* BEVERAGES */}
   <Section id="beverages" eyebrow="Cruise beverage options" heading="Bar" headingEm="and beverage packages." testid="rates-beverages">
    <Accordion type="multiple" defaultValue={["pkg", "tab"]} className="space-y-3">
     <RateAccordion value="pkg" label="Beverage package, $15.50 per person per hour" data-testid="rates-acc-bev-pkg">
      <div className="space-y-5">
       <p className="text-white/80 text-base">Standard beverage package across the cruise duration.</p>
       <div className="space-y-3">
        <MenuLine label="Beers" items={["Carlton Dry", "Great Northern Super Crisp", "Cascade Light"]} />
        <MenuLine label="White wine" items={["Wolf Blass Eaglehawk Sauvignon Blanc"]} />
        <MenuLine label="Red wine" items={["Wolf Blass Eaglehawk Cabernet Sauvignon"]} />
        <MenuLine label="Sparkling" items={["Wolf Blass Eaglehawk Cuvée"]} />
        <MenuLine label="Non alcoholic" items={["Coca-Cola, soft drinks, fresh juice"]} />
       </div>
       <p className="text-white/60 text-sm italic">No spirit packages, all spirits available via bar tab or individual purchase.</p>
      </div>
     </RateAccordion>
     <RateAccordion value="tab" label="Bar via tab or individual purchase" data-testid="rates-acc-bev-tab">
      <ul className="space-y-2 text-white/85 text-base mb-6">
       <li className="flex items-start gap-3"><Check className="w-4 h-4 mt-1 shrink-0" style={{ color: accent }} /><span>Individual purchase (cash bar only on board)</span></li>
       <li className="flex items-start gap-3"><Check className="w-4 h-4 mt-1 shrink-0" style={{ color: accent }} /><span>Bar tab + 1 bar staff required for bar tab ($275 per staff)</span></li>
       <li className="flex items-start gap-3"><Check className="w-4 h-4 mt-1 shrink-0" style={{ color: accent }} /><span>Pre-paid bar tab available</span></li>
       <li className="flex items-start gap-3"><Check className="w-4 h-4 mt-1 shrink-0" style={{ color: accent }} /><span>Minimum $500 bar spend</span></li>
      </ul>
      <div className="space-y-3">
       <PriceLine label="Beers" price="$10 per bottle" detail="Carlton Dry · Great Northern Super Crisp · Cascade Light" />
       <PriceLine label="White wine" price="$9 per glass" detail="Wolf Blass Eaglehawk Sauvignon Blanc" />
       <PriceLine label="Red wine" price="$9 per glass" detail="Wolf Blass Eaglehawk Cabernet Sauvignon" />
       <PriceLine label="Sparkling wine" price="$9 per glass" detail="Wolf Blass Eaglehawk Cuvée" />
       <PriceLine label="Non alcoholic" price="$4 per glass" detail="Soft drinks & orange juice" />
       <PriceLine label="Spirits" price="$14 per glass" detail="Bundaberg Rum · Jack Daniels · Johnnie Walker Red · Jim Beam · Vodka · Gordon's Gin · Canadian Club" />
      </div>
     </RateAccordion>
    </Accordion>
   </Section>

   {/* RELATED PACKAGES */}
   <section className="relative py-20 lg:py-24 px-5 lg:px-10 border-y border-white/10" style={{ background: dark2 }} data-testid="rates-related-packages">
    <div className="mx-auto max-w-7xl">
     <Eyebrow>Themed cruise packages</Eyebrow>
     <h2 className="font-black tracking-tight leading-[0.96] mt-5" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
      For dedicated date packages,
      <em className="font-light italic ml-2" style={{ color: tint }}>see the themed cruise pages.</em>
     </h2>
     <p className="text-white/70 mt-5 text-base lg:text-lg font-light max-w-3xl">For seasonal cruises (Christmas, New Year's Eve, Boxing Day, Australia Day, Vivid) plus weddings and BYO, the full inclusions and ticket prices live on the dedicated cruise pages below.</p>
     <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
      {[
       { to: "/cruises/wedding-cruises-sydney-harbour", label: "Wedding cruises Sydney" },
       { to: "/cruises/christmas-party-cruises-sydney", label: "Christmas cruise packages" },
       { to: "/cruises/byo-cruise-sydney-harbour", label: "BYO cruise packages" },
       { to: "/cruises/nye-cruises-sydney-harbour", label: "New Year's Eve cruises" },
       { to: "/cruises/boxing-day-cruises-sydney", label: "Boxing Day cruises" },
       { to: "/cruises/australia-day-cruises-sydney-harbour", label: "Australia Day cruises" },
       { to: "/cruises/vivid-cruises-sydney", label: "Vivid cruises Sydney" },
       { to: "/cruises/corporate-cruises-sydney-harbour", label: "Corporate cruises Sydney" }].map(({ to, label }, i) => (
       <Link key={i} to={to} className="flex items-center justify-between gap-4 px-6 py-5 rounded-2xl border border-white/12 hover:border-white/30 transition-all hover:scale-[1.01] bg-white/[0.025]" data-testid={`rates-related-${i}`}>
        <span className="text-white font-bold text-sm uppercase tracking-[0.14em]">{label}</span>
        <ArrowRight className="w-4 h-4 shrink-0 text-white/55" />
       </Link>
      ))}
     </div>
    </div>
   </section>

   {/* FINAL CTA */}
   <section className="relative py-24 lg:py-32 px-5 lg:px-10 overflow-hidden" style={{ background: `linear-gradient(180deg, ${dark1} 0%, ${dark2} 100%)` }}>
    <Starfield density={18} color={star} glow={`${star}55`} fixed={false} intensity={0.35} />
    <div className="mx-auto max-w-3xl text-center relative z-10">
     <Eyebrow>Tailored quote</Eyebrow>
     <h2 className="font-black tracking-tight leading-[0.96] mt-5" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 5vw, 3.6rem)" }}>
      Pick your menu, pick your bar, <em className="font-light italic" style={{ color: accent }}>we'll quote inside 24 hours.</em>
     </h2>
     <p className="text-white/75 text-base lg:text-lg mt-6 font-light">Every Magic Cruises booking is a private full boat charter, combining vessel hire, catering, beverages, staff and any add-ons into a single tailored quote. Send the date, the guest count and the brief, our team comes back within 24 hours with the full breakdown.</p>
     <div className="flex flex-wrap justify-center gap-4 mt-10">
      <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase hover:scale-[1.03] transition-all text-[#0A0A0A]" style={{ background: `linear-gradient(135deg, ${tint} 0%, ${accent} 100%)`, boxShadow: `0 20px 50px -10px ${accent}88` }} data-testid="rates-final-cta-enquire">
       Send an enquiry <ArrowRight className="w-4 h-4" />
      </Link>
      <a href={`tel:${CONTACT.phone1}`} className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-black text-xs lg:text-sm tracking-[0.18em] uppercase border border-white/25 hover:bg-white/8 transition-all text-white" data-testid="rates-final-cta-call">
       <Phone className="w-4 h-4" /> Call {CONTACT.phone1}
      </a>
     </div>
    </div>
   </section>
  </div>
 );
}

/* ---------------- helpers ---------------- */

function Eyebrow({ children }) {
 return (
  <div className="inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.42em] font-black">
   <span className="block w-6 h-px" style={{ backgroundColor: `${accent}99` }} />
   <span style={{ color: accent }}>{children}</span>
   <span className="block w-6 h-px" style={{ backgroundColor: `${accent}99` }} />
  </div>
 );
}

function Section({ id, eyebrow, heading, headingEm, children, testid }) {
 return (
  <section id={id} className="relative py-20 lg:py-28 px-5 lg:px-10 scroll-mt-24" data-testid={testid}>
   <div className="mx-auto max-w-4xl">
    <Eyebrow>{eyebrow}</Eyebrow>
    <h2 className="font-black tracking-tight leading-[0.96] mt-5" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.8rem, 4vw, 3rem)" }}>
     {heading} <em className="font-light italic" style={{ color: accent }}>{headingEm}</em>
    </h2>
    <div className="mt-10">{children}</div>
   </div>
  </section>
 );
}

function JumpButton({ href, icon: Icon, label, sub, gradient, shadow, testid }) {
 const handleClick = (e) => {
  const target = document.querySelector(href);
  if (target) {
   e.preventDefault();
   target.scrollIntoView({ behavior: "smooth", block: "start" });
   if (history.pushState) history.pushState(null, "", href);
  }
 };
 return (
  <a
   href={href}
   onClick={handleClick}
   data-testid={testid}
   className="group relative flex items-center gap-5 px-6 py-6 rounded-2xl text-[#0A0A0A] font-black hover:scale-[1.03] active:scale-[0.99] transition-all overflow-hidden min-h-[92px]"
   style={{ background: gradient, boxShadow: shadow }}
  >
   <span className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" style={{ background: "linear-gradient(120deg, transparent 30%, rgba(255,255,255,0.35) 50%, transparent 70%)" }} />
   <span className="relative shrink-0 w-12 h-12 rounded-full flex items-center justify-center bg-black/15 backdrop-blur-sm">
    <Icon className="w-[22px] h-[22px]" strokeWidth={2} />
   </span>
   <span className="relative flex-1 min-w-0 text-left flex flex-col gap-2">
    <span className="block text-sm lg:text-[15px] uppercase tracking-[0.16em] leading-tight">{label}</span>
    <span className="block text-[10px] lg:text-[11px] tracking-[0.06em] opacity-75 font-semibold leading-snug">{sub}</span>
   </span>
   <ChevronDown className="relative w-5 h-5 shrink-0 group-hover:translate-y-1 transition-transform" strokeWidth={2.5} />
  </a>
 );
}

function RateAccordion({ value, label, children, "data-testid": testid }) {
 return (
  <AccordionItem value={value} className="border border-white/12 rounded-2xl px-5 lg:px-7 bg-white/[0.02] hover:bg-white/[0.04] transition-colors data-[state=open]:border-white/25 data-[state=open]:bg-white/[0.04]" data-testid={testid}>
   <AccordionTrigger className="font-black text-base lg:text-lg uppercase tracking-[0.10em] py-5 hover:no-underline text-white">{label}</AccordionTrigger>
   <AccordionContent className="pb-6 pt-2">{children}</AccordionContent>
  </AccordionItem>
 );
}

function MenuAccordion({ value, title, price, sections, highlight = false }) {
 return (
  <AccordionItem value={value} className={`border rounded-2xl px-5 lg:px-7 transition-colors ${highlight ? "border-[hsl(45,100%,72%)]/35 bg-[hsl(45,100%,72%)]/[0.04]" : "border-white/12 bg-white/[0.02]"} hover:bg-white/[0.05] data-[state=open]:border-white/25`} data-testid={`rates-menu-${value}`}>
   <AccordionTrigger className="py-5 hover:no-underline">
    <div className="flex-1 text-left">
     <div className="font-black text-base lg:text-lg uppercase tracking-[0.10em] text-white">{title}</div>
     <div className="text-[12px] uppercase tracking-[0.18em] font-bold mt-1.5" style={{ color: highlight ? accent : "rgba(255,255,255,0.55)" }}>{price}</div>
    </div>
   </AccordionTrigger>
   <AccordionContent className="pb-6 pt-2">
    <div className="space-y-6">
     {sections.map((sec, i) => (
      <div key={i}>
       {sec.heading && (
        <div className="text-[10px] uppercase tracking-[0.32em] font-black mb-3" style={{ color: highlight ? accent : tint }}>{sec.heading}</div>
       )}
       <ul className="space-y-2">
        {sec.items.map((item, j) => (
         <li key={j} className="text-white/80 text-[14px] leading-snug pl-3 border-l-2" style={{ borderColor: highlight ? `${accent}55` : `${tint}55` }}>{item}</li>
        ))}
       </ul>
      </div>
     ))}
    </div>
   </AccordionContent>
  </AccordionItem>
 );
}

function RateGrid({ items }) {
 return (
  <div className="grid sm:grid-cols-2 gap-3">
   {items.map(([label, price], i) => (
    <div key={i} className="flex items-baseline justify-between gap-4 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/8">
     <span className="text-white/85 text-sm lg:text-base">{label}</span>
     <span className="text-white font-black text-base lg:text-lg whitespace-nowrap" style={{ color: accent }}>{price}</span>
    </div>
   ))}
  </div>
 );
}

function MenuLine({ label, items }) {
 return (
  <div className="px-4 py-3 rounded-xl bg-white/[0.03] border border-white/8">
   <div className="text-[10px] uppercase tracking-[0.32em] font-black mb-1.5" style={{ color: accent }}>{label}</div>
   <div className="text-white/80 text-sm">{items.join(" · ")}</div>
  </div>
 );
}

function PriceLine({ label, price, detail }) {
 return (
  <div className="flex items-center justify-between gap-4 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/8 flex-wrap">
   <div className="min-w-[140px]">
    <div className="text-[10px] uppercase tracking-[0.32em] font-black" style={{ color: accent }}>{label}</div>
    <div className="text-white/65 text-xs mt-1">{detail}</div>
   </div>
   <div className="text-white font-black text-base whitespace-nowrap" style={{ color: tint }}>{price}</div>
  </div>
 );
}

const FLOAT_SECTIONS = [
 { id: "vessel-hire", label: "Rates", full: "Vessel Hire Rates", icon: Anchor, color: tint },
 { id: "menus", label: "Menus", full: "Menus", icon: UtensilsCrossed, color: hot },
 { id: "beverages", label: "Bar", full: "Beverage Package", icon: Wine, color: accent },
];

function FloatingSectionNav() {
 const [active, setActive] = useState("");
 const [visible, setVisible] = useState(false);

 useEffect(() => {
  // Show only after the user scrolls past the hero (~500px)
  const onScroll = () => setVisible(window.scrollY > 520);
  onScroll();
  window.addEventListener("scroll", onScroll, { passive: true });
  return () => window.removeEventListener("scroll", onScroll);
 }, []);

 useEffect(() => {
  const ids = FLOAT_SECTIONS.map((s) => s.id);
  const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
  if (els.length === 0) return;
  const obs = new IntersectionObserver(
   (entries) => {
    // pick the entry closest to the top that's intersecting
    const visibles = entries.filter((e) => e.isIntersecting);
    if (visibles.length > 0) {
     visibles.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
     setActive(visibles[0].target.id);
    }
   },
   { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
  );
  els.forEach((el) => obs.observe(el));
  return () => obs.disconnect();
 }, []);

 const handleJump = (e, id) => {
  e.preventDefault();
  const target = document.getElementById(id);
  if (target) {
   target.scrollIntoView({ behavior: "smooth", block: "start" });
   if (history.pushState) history.pushState(null, "", `#${id}`);
  }
 };

 return (
  <>
   {/* Desktop: vertical pill on right edge */}
   <nav
    aria-label="Section navigation"
    data-testid="rates-floating-nav-desktop"
    className={`hidden lg:flex fixed right-6 top-1/2 -translate-y-1/2 z-40 flex-col gap-2 p-2 rounded-3xl border border-white/15 backdrop-blur-xl transition-all duration-500 ${
     visible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6 pointer-events-none"
    }`}
    style={{ background: "rgba(10,10,18,0.78)", boxShadow: "0 30px 80px -20px rgba(0,0,0,0.6)" }}
   >
    <div className="text-[9px] uppercase tracking-[0.28em] font-black text-white/45 px-3 pt-2 pb-1">Jump to</div>
    {FLOAT_SECTIONS.map(({ id, full, icon: Icon, color }) => {
     const isActive = active === id;
     return (
      <a
       key={id}
       href={`#${id}`}
       onClick={(e) => handleJump(e, id)}
       data-testid={`floating-nav-${id}`}
       className={`group flex items-center gap-3 px-3 py-3 rounded-2xl transition-all ${
        isActive ? "bg-white/10" : "hover:bg-white/[0.06]"
       }`}
      >
       <span
        className="shrink-0 w-9 h-9 rounded-xl flex items-center justify-center transition-all"
        style={{
         background: isActive ? color : "rgba(255,255,255,0.06)",
         color: isActive ? "#0A0A0A" : color,
         boxShadow: isActive ? `0 10px 24px -8px ${color}` : "none",
        }}
       >
        <Icon className="w-[18px] h-[18px]" strokeWidth={2} />
       </span>
       <span
        className={`text-[12px] uppercase tracking-[0.16em] font-black whitespace-nowrap transition-colors ${
         isActive ? "text-white" : "text-white/70 group-hover:text-white"
        }`}
       >
        {full}
       </span>
      </a>
     );
    })}
   </nav>

   {/* Mobile: bottom dock */}
   <nav
    aria-label="Section navigation"
    data-testid="rates-floating-nav-mobile"
    className={`lg:hidden fixed left-3 right-3 bottom-3 z-40 flex items-center justify-between gap-2 p-2 rounded-2xl border border-white/15 backdrop-blur-xl transition-all duration-500 ${
     visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6 pointer-events-none"
    }`}
    style={{ background: "rgba(10,10,18,0.85)", boxShadow: "0 20px 50px -10px rgba(0,0,0,0.7)" }}
   >
    {FLOAT_SECTIONS.map(({ id, label, icon: Icon, color }) => {
     const isActive = active === id;
     return (
      <a
       key={id}
       href={`#${id}`}
       onClick={(e) => handleJump(e, id)}
       data-testid={`floating-nav-mobile-${id}`}
       className={`flex-1 flex flex-col items-center justify-center gap-1 py-2 rounded-xl transition-all ${
        isActive ? "bg-white/10" : ""
       }`}
       style={isActive ? { boxShadow: `inset 0 0 0 1px ${color}66` } : {}}
      >
       <Icon className="w-[18px] h-[18px]" strokeWidth={2} style={{ color: isActive ? color : "rgba(255,255,255,0.65)" }} />
       <span className={`text-[10px] uppercase tracking-[0.14em] font-black ${isActive ? "text-white" : "text-white/65"}`}>{label}</span>
      </a>
     );
    })}
   </nav>
  </>
 );
}
